import * as React from 'react';
import styled, { keyframes } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import { createUIContext } from '../../internals/createContext';
import { useLayerZIndex } from '../../internals/LayerManager';
import { Presence } from '../../internals/Presence';
import { Portal } from '../../internals/Portal';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Slot } from '../../internals/Slot';
import { useControllable } from '../../internals/useControllable';
import { useFocusTrap } from '../../internals/useFocusTrap';
import { useId } from '../../internals/useId';
import { useScrollLock } from '../../internals/useScrollLock';
import { Button } from '../../primitives/Button/Button';
import type { ButtonVariant } from '../../primitives/Button/buttonTypes';
import { TextArea } from '../../primitives/TextArea/TextArea';
import type { TextAreaProps } from '../../primitives/TextArea/TextArea';
import { useTheme } from '../../theme/ThemeProvider';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

import {
  actionButtonVariantForPreset,
  type AlertDialogVariant,
  presetActionLabel,
  presetAlertDescription,
  presetAlertTitle,
  presetCancelLabel,
} from './alertDialogPresets';

export type { AlertDialogVariant } from './alertDialogPresets';

/* ─────────────────────────── Context ───────────────────────────────────── */

export interface AlertDialogRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  /**
   * Preset workflow — drives default title/description/button labels
   * when using `preset` on Title/Description or omitting button children.
   */
  variant?: AlertDialogVariant;
  /** Interpolated into preset title (e.g. entity name, participant name). */
  subject?: string;
  /** When true, primary action is blocked until remarks are non-empty. */
  remarksRequired?: boolean;
  /** Controlled remarks text. */
  remarks?: string;
  /** Uncontrolled remarks initial value. */
  defaultRemarks?: string;
  onRemarksChange?: (value: string) => void;
}

interface AlertDialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
  remarksId: string;
  variant: AlertDialogVariant;
  subject?: string;
  remarksRequired: boolean;
  remarks: string;
  setRemarks: (value: string) => void;
  remarksError: boolean;
  setRemarksError: (value: boolean) => void;
  validateBeforeClose: () => boolean;
}

const [AlertDialogProvider, useAlertDialogContext] =
  createUIContext<AlertDialogContextValue>('AlertDialog');

/* ───────────────────────── Root ────────────────────────────────────────── */

const AlertDialogRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  children,
  variant = 'default',
  subject,
  remarksRequired = false,
  remarks: remarksProp,
  defaultRemarks = '',
  onRemarksChange,
}: AlertDialogRootProps) => {
  const [isOpen, setOpen] = useControllable<boolean>({
    value: open,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });

  const [remarksInternal, setRemarksInternal] = React.useState(defaultRemarks);
  const remarks = remarksProp !== undefined ? remarksProp : remarksInternal;
  const setRemarks = React.useCallback(
    (value: string) => {
      onRemarksChange?.(value);
      if (remarksProp === undefined) setRemarksInternal(value);
    },
    [remarksProp, onRemarksChange]
  );

  const [remarksError, setRemarksError] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setRemarksError(false);
      if (remarksProp === undefined) setRemarksInternal(defaultRemarks);
    }
  }, [isOpen, remarksProp, defaultRemarks]);

  const titleId = useId('alertdialog-title');
  const descriptionId = useId('alertdialog-description');
  const remarksId = useId('alertdialog-remarks');

  const validateBeforeClose = React.useCallback(() => {
    if (!remarksRequired) {
      setRemarksError(false);
      return true;
    }
    if (!remarks.trim()) {
      setRemarksError(true);
      return false;
    }
    setRemarksError(false);
    return true;
  }, [remarksRequired, remarks]);

  const value = React.useMemo(
    () => ({
      open: isOpen,
      onOpenChange: setOpen,
      titleId,
      descriptionId,
      remarksId,
      variant,
      subject,
      remarksRequired,
      remarks,
      setRemarks,
      remarksError,
      setRemarksError,
      validateBeforeClose,
    }),
    [
      isOpen,
      setOpen,
      titleId,
      descriptionId,
      remarksId,
      variant,
      subject,
      remarksRequired,
      remarks,
      setRemarks,
      remarksError,
      validateBeforeClose,
    ]
  );

  return <AlertDialogProvider value={value}>{children}</AlertDialogProvider>;
};

AlertDialogRoot.displayName = 'AlertDialog.Root';

/* ───────────────────────── Trigger ─────────────────────────────────────── */

export interface AlertDialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
}

const AlertDialogTrigger = React.forwardRef<HTMLButtonElement, AlertDialogTriggerProps>(
  function AlertDialogTrigger(
    { asChild = false, semanticId, onClick, type = 'button', ...rest },
    ref
  ) {
    const { open, onOpenChange } = useAlertDialogContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(true);
    };

    if (asChild) {
      return (
        <Slot
          ref={ref as React.Ref<HTMLElement>}
          data-state={open ? 'open' : 'closed'}
          onClick={handleClick as React.MouseEventHandler<HTMLElement>}
          {...semanticProps}
          {...(rest as unknown as React.ComponentProps<typeof Slot>)}
        />
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      />
    );
  }
);

AlertDialogTrigger.displayName = 'AlertDialog.Trigger';

/* ───────────────────────── Portal ──────────────────────────────────────── */

export interface AlertDialogPortalProps {
  children: React.ReactNode;
}

/**
 * Stacking root that re-injects theme CSS variables.
 * `isolation: isolate` scopes internal z-indexes.
 * z-index is applied dynamically via LayerManager.
 */
const AlertDialogPortalStack = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  isolation: isolate;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const AlertDialogPortal = ({ children }: AlertDialogPortalProps) => {
  const { theme } = useTheme();
  const layerZ = useLayerZIndex('alert');
  return (
    <Portal>
      <AlertDialogPortalStack
        data-wl-alert-stack=''
        style={{ ...theme.cssVars as React.CSSProperties, zIndex: layerZ }}
      >
        {children}
      </AlertDialogPortalStack>
    </Portal>
  );
};

AlertDialogPortal.displayName = 'AlertDialog.Portal';

/* ───────────────────────── Overlay ─────────────────────────────────────── */

const backdropIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const backdropOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  background: var(--ui-overlay, rgba(22, 22, 22, 0.85));
  opacity: 0;
  visibility: hidden;

  &[data-state='open'] {
    animation: ${backdropIn} 300ms ease-in-out forwards;
    opacity: 1;
    visibility: visible;
  }

  &[data-state='closed'] {
    animation: ${backdropOut} 300ms ease-in-out forwards;
  }
`;

export interface AlertDialogOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const AlertDialogOverlay = React.forwardRef<HTMLDivElement, AlertDialogOverlayProps>(
  function AlertDialogOverlay({ semanticId, onClick, ...rest }, ref) {
    const { open } = useAlertDialogContext();
    useScrollLock(open);
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <Presence present={open} animateEnter>
        <StyledOverlay ref={ref} onClick={onClick} {...semanticProps} {...rest} />
      </Presence>
    );
  }
);

AlertDialogOverlay.displayName = 'AlertDialog.Overlay';

/* ───────────────────────── Content ─────────────────────────────────────── */

/**
 * Dialog card — uses theme tokens with palette fallbacks for resilience.
 * Dimensions match production `alertPopup.styled.ts` `.popupContent`.
 * Incorporates positioner styles (fixed centering) so a single Presence
 * wrapper can detect its transition and properly animate on close.
 */
const StyledContent = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  transform: scale(0.96);
  transition: opacity 200ms ease-out, transform 200ms ease-out;

  &[data-state='open'] {
    opacity: 1;
    transform: scale(1);
  }

  &[data-state='closed'] {
    opacity: 0;
    transform: scale(0.96);
  }
`;

const StyledCard = styled.div`
  pointer-events: auto;
  width: auto;
  max-width: ${rem(840)};
  max-height: calc(100vh - ${rem(32)});
  overflow: auto;
  margin: ${rem(5)};
  padding: ${rem(20)} ${rem(20)};
  background: var(--ui-surface, ${palette.White});
  border: none;
  border-radius: var(--ui-radius-md, ${rem(6)});
  box-shadow: 0px ${rem(2)} ${rem(7)} rgba(210, 210, 210, 0.28);
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);

  @media screen and (min-width: 576px) {
    min-width: ${rem(515)};
    margin: ${rem(28)} auto;
    padding: ${rem(24)} ${rem(42)};
  }
`;

export interface AlertDialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const AlertDialogContent = React.forwardRef<HTMLDivElement, AlertDialogContentProps>(
  function AlertDialogContent({ semanticId, children, ...rest }, ref) {
    const { open, titleId, descriptionId } = useAlertDialogContext();
    const focusTrapRef = useFocusTrap(open);
    const composedRef = composeRefs(ref, focusTrapRef);
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <Presence present={open} animateEnter>
        <StyledContent>
          <StyledCard
            ref={composedRef}
            role='alertdialog'
            aria-modal='true'
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            tabIndex={-1}
            {...semanticProps}
            {...rest}>
            {children}
          </StyledCard>
        </StyledContent>
      </Presence>
    );
  }
);

AlertDialogContent.displayName = 'AlertDialog.Content';

/* ───────────────────────── Cancel ──────────────────────────────────────── */

export type AlertDialogCancelProps = Omit<React.ComponentProps<typeof Button>, 'onClick'> & {
  semanticId?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

const AlertDialogCancel = React.forwardRef<HTMLButtonElement, AlertDialogCancelProps>(
  function AlertDialogCancel(
    {
      semanticId,
      type = 'button',
      variant = 'primary-dark',
      onClick,
      children,
      loading,
      disabled,
      ...rest
    },
    ref
  ) {
    const { onOpenChange, variant: flowVariant } = useAlertDialogContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const label = children ?? presetCancelLabel(flowVariant);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(false);
    };

    return (
      <Button
        ref={ref}
        type={type}
        variant={variant}
        onClick={handleClick}
        loading={loading}
        disabled={loading || disabled}
        data-state='cancel'
        {...semanticProps}
        {...rest}>
        {label}
      </Button>
    );
  }
);

AlertDialogCancel.displayName = 'AlertDialog.Cancel';

/* ───────────────────────── Action ──────────────────────────────────────── */

export interface AlertDialogActionProps
  extends Omit<React.ComponentProps<typeof Button>, 'onClick' | 'variant'> {
  semanticId?: string;
  /**
   * Semantic tone — `destructive` maps to `primary-danger`, `default` maps to `primary`.
   * Ignored when `buttonVariant` is set.
   */
  variant?: 'default' | 'destructive';
  /** Explicit DS ButtonVariant — overrides tone mapping. */
  buttonVariant?: ButtonVariant;
  /** When false, the dialog stays open after clicking (useful for async handlers). */
  closeOnClick?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const AlertDialogAction = React.forwardRef<HTMLButtonElement, AlertDialogActionProps>(
  function AlertDialogAction(
    {
      semanticId,
      variant: toneProp,
      buttonVariant: buttonVariantProp,
      type = 'button',
      onClick,
      children,
      closeOnClick = true,
      loading,
      disabled,
      icon: iconProp,
      ...rest
    },
    ref
  ) {
    const { onOpenChange, variant: rootVariant, validateBeforeClose } = useAlertDialogContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const effectiveTone = toneProp ?? actionButtonVariantForPreset(rootVariant);
    const resolvedButtonVariant: ButtonVariant =
      buttonVariantProp ?? (effectiveTone === 'destructive' ? 'primary-danger' : 'primary');

    const label = children ?? presetActionLabel(rootVariant);
    const usePresetIcon = children == null && iconProp === undefined;
    const confirmIcon = usePresetIcon ? 'check_circle' : iconProp;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (!closeOnClick) return;
      if (!validateBeforeClose()) return;
      onOpenChange(false);
    };

    return (
      <Button
        ref={ref}
        type={type}
        variant={resolvedButtonVariant}
        icon={confirmIcon}
        onClick={handleClick}
        loading={loading}
        disabled={loading || disabled}
        data-state='action'
        data-tone={effectiveTone}
        {...semanticProps}
        {...rest}>
        {label}
      </Button>
    );
  }
);

AlertDialogAction.displayName = 'AlertDialog.Action';

/* ───────────────────────── Title ───────────────────────────────────────── */

/** Production title: h5 (20px / 24px) + Tailwind `mb-5` = 1.25rem bottom margin. */
const DialogHeading = styled.h2<{ $align?: 'left' | 'center' | 'right' }>`
  margin: 0 0 ${rem(12)};
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  letter-spacing: -0.01em;
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  text-align: ${({ $align }) => $align ?? 'left'};
`;

export interface AlertDialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  semanticId?: string;
  /** Renders title from Root `variant` + `subject`. Children override when both are present. */
  preset?: boolean;
  /** Text alignment. Defaults to `'left'`. */
  align?: 'left' | 'center' | 'right';
}

const AlertDialogTitle = React.forwardRef<HTMLHeadingElement, AlertDialogTitleProps>(
  function AlertDialogTitle({ semanticId, preset = false, align, children, ...rest }, ref) {
    const { titleId, variant, subject } = useAlertDialogContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const text = children ?? (preset ? presetAlertTitle(variant, subject) : undefined);

    return (
      <DialogHeading ref={ref} $align={align} {...semanticProps} {...rest} id={titleId}>
        {text}
      </DialogHeading>
    );
  }
);

AlertDialogTitle.displayName = 'AlertDialog.Title';

/* ───────────────────────── Description ─────────────────────────────────── */

/** Production subtitle: h6 (16px / 18px), muted. No extra bottom margin — footer provides gap. */
const DialogBodyText = styled.p<{ $align?: 'left' | 'center' | 'right' }>`
  margin: 0;
  font-size: ${rem(16)};
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.Gray});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  text-align: ${({ $align }) => $align ?? 'left'};
  white-space: pre-line;
`;

export interface AlertDialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  semanticId?: string;
  preset?: boolean;
  /** Text alignment. Defaults to `'left'`. */
  align?: 'left' | 'center' | 'right';
}

const AlertDialogDescription = React.forwardRef<HTMLParagraphElement, AlertDialogDescriptionProps>(
  function AlertDialogDescription({ semanticId, preset = false, align, children, ...rest }, ref) {
    const { descriptionId, variant } = useAlertDialogContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const auto = preset ? presetAlertDescription(variant) : undefined;
    const text = children ?? auto;

    if (text == null || text === '') {
      return <DialogBodyText ref={ref} $align={align} id={descriptionId} hidden {...semanticProps} {...rest} />;
    }

    return (
      <DialogBodyText ref={ref} $align={align} {...semanticProps} {...rest} id={descriptionId}>
        {text}
      </DialogBodyText>
    );
  }
);

AlertDialogDescription.displayName = 'AlertDialog.Description';

/* ───────────────────────── Remarks ─────────────────────────────────────── */

const RemarksLabel = styled.label`
  display: block;
  font-size: ${rem(13)};
  font-weight: 500;
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  margin-bottom: ${rem(6)};
`;

export interface AlertDialogRemarksProps extends Omit<TextAreaProps, 'id'> {
  /** Field label (default "Remarks"). */
  label?: string;
  readOnly?: boolean;
}

const AlertDialogRemarks = React.forwardRef<HTMLTextAreaElement, AlertDialogRemarksProps>(
  function AlertDialogRemarks(
    { label = 'Remarks', semanticId, readOnly, error, onChange, rows = 4, ...rest },
    ref
  ) {
    const { remarksId, remarks, setRemarks, remarksError, setRemarksError, remarksRequired } =
      useAlertDialogContext();

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setRemarks(event.target.value);
      setRemarksError(false);
      onChange?.(event);
    };

    return (
      <div style={{ marginTop: rem(12) }}>
        <RemarksLabel htmlFor={remarksId}>
          {label}
          {!readOnly && remarksRequired ? (
            <span style={{ color: 'var(--ui-status-danger)' }}> *</span>
          ) : null}
        </RemarksLabel>
        <TextArea
          ref={ref}
          id={remarksId}
          semanticId={semanticId}
          value={remarks}
          onChange={handleChange}
          readOnly={readOnly}
          disabled={readOnly}
          error={remarksError || error}
          rows={rows}
          placeholder='Enter Reason'
          aria-invalid={remarksError || undefined}
          {...rest}
        />
        {remarksError ? (
          <p
            style={{
              margin: `${rem(6)} 0 0`,
              fontSize: rem(12),
              color: 'var(--ui-status-danger)',
            }}>
            Required field
          </p>
        ) : null}
      </div>
    );
  }
);

AlertDialogRemarks.displayName = 'AlertDialog.Remarks';

/* ───────────────────────── Footer ──────────────────────────────────────── */

/**
 * Button row matching production `.popupButtons`.
 * Overrides DS Button geometry to match the production alert sizing:
 * font-size 14px, line-height 16px, padding 8px 16px.
 */
const StyledFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${rem(8)};
  margin-top: ${rem(18)};

  button {
    margin: 0 ${rem(4)};
    font-size: ${rem(14)};
    line-height: ${rem(16)};
    padding: ${rem(8)} ${rem(16)};
    min-height: auto;

    .material-symbols-outlined {
      font-size: ${rem(15)};
      width: ${rem(15)};
      height: ${rem(15)};
    }
  }
`;

export interface AlertDialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const AlertDialogFooter = React.forwardRef<HTMLDivElement, AlertDialogFooterProps>(
  function AlertDialogFooter({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledFooter ref={ref} {...semanticProps} {...rest} />;
  }
);

AlertDialogFooter.displayName = 'AlertDialog.Footer';

/* ───────────────────────── Compound export ─────────────────────────────── */

export const AlertDialog = {
  Root: AlertDialogRoot,
  Trigger: AlertDialogTrigger,
  Portal: AlertDialogPortal,
  Overlay: AlertDialogOverlay,
  Content: AlertDialogContent,
  Cancel: AlertDialogCancel,
  Action: AlertDialogAction,
  Title: AlertDialogTitle,
  Description: AlertDialogDescription,
  Remarks: AlertDialogRemarks,
  Footer: AlertDialogFooter,
};

/** For `@packages/ui/integrations/ConfirmDialog` — not part of the public barrel export. */
export { useAlertDialogContext };
